import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/i18n";
import { LEGAL } from "@/data/legal";
import { FAQ_CATEGORIES, ACCOUNTS } from "@/data/site";
import api from "@/lib/api";

/* Friendly labels for breadcrumb URL segments. */
const BREADCRUMB_LABELS = {
  "company-news": "Company News",
  "fsc-mauritius-licence": "FSC Mauritius Licence",
  "account-types": "Account Types",
  "how-it-works": "How It Works",
  "first-deposit-bonus": "First Deposit Bonus",
  "calculator": "Trading Calculators",
  "compare": "Compare Accounts",
  "account-comparison": "Account Comparison",
  "platforms": "Platforms",
  "mt5": "MetaTrader 5",
  "desktop": "MT5 for Windows",
  "mac": "MT5 for Mac",
  "web": "MT5 Web Terminal",
  "mobile": "MT5 Mobile",
  "vps": "Free VPS",
  "copy-trading": "Copy Trading",
  "algo": "Algo & EAs",
  "markets": "Markets",
  "forex": "Forex",
  "metals": "Metals",
  "indices": "Indices",
  "energies": "Energies",
  "shares": "Shares CFDs",
  "crypto": "Crypto",
  "live-spreads": "Live Spreads",
  "market-hours": "Market Hours",
  "sentiment": "Market Sentiment",
  "economic-calendar": "Economic Calendar",
  "symbols": "Symbol Specifications",
  "glossary": "Trading Glossary",
  "leverage": "Leverage",
  "swap-rates": "Swap Rates",
  "funding": "Funding Methods",
  "withdrawals": "Withdrawals",
  "regulation": "Regulation",
  "safety-of-funds": "Safety of Funds",
  "why-ofinancial": "Why OFinancial",
  "support": "Support",
  "reviews": "Client Reviews",
  "faq": "FAQ",
  "about": "About",
  "contact": "Contact",
  "legal": "Legal",
  "terms": "Terms & Conditions",
  "client-agreement": "Client Agreement",
  "aml": "AML Policy",
  "bonus-terms": "Bonus Terms",
  "privacy": "Privacy Policy",
  "cookies": "Cookie Policy",
  "refund": "Refund Policy",
};

function labelFor(seg) {
  return (
    BREADCRUMB_LABELS[seg] ||
    seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/* -- DOM helpers -- */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertHreflangLinks(path) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  const origin = `${window.location.protocol}//${window.location.host}`;
  LANGUAGES.forEach((l) => {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", l.code);
    link.setAttribute("href", l.code === "en" ? `${origin}${path}` : `${origin}${path}?lang=${l.code}`);
    document.head.appendChild(link);
  });
  const xd = document.createElement("link");
  xd.setAttribute("rel", "alternate");
  xd.setAttribute("hreflang", "x-default");
  xd.setAttribute("href", `${origin}${path}`);
  document.head.appendChild(xd);
}

/* Replace or insert a JSON-LD script with a fixed id. */
function upsertJsonLd(id, jsonObj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(jsonObj);
}
function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/* -- Per-route copy. -- */
const ROUTE_SEO = {
  "/": null,
  "/account-types": {
    title: "Accounts Overview | Classic, Premier, Elite, Social | OFinancial Markets",
    description: "Pick the account that matches your style. Classic, Premier and Elite raw-spread accounts plus Social copy-trading on MT5. Spreads from 0.0 pips · Leverage up to 1:1000 · From $50.",
    keywords: "broker account types, accounts overview, raw spread account, classic account, premier account, elite account, MT5 broker account, copy trading account",
  },
  "/first-deposit-bonus": {
    title: "50% First Deposit Bonus | Tradable Credit | OFinancial Markets",
    description: "Boost your first deposit by 50% on any live trading account. Tradable credit, transparent terms, fully refundable after the qualifying volume.",
    keywords: "first deposit bonus, 50% bonus broker, MT5 deposit bonus, OFinancial bonus, trading credit",
  },
  "/about": {
    title: "About Us | A Broker Built for Serious Traders | OFinancial Markets",
    description: "OFinancial Markets is a regulated multi-asset broker. Read about our mission, leadership and the standards we apply to spreads, execution and client funds.",
    keywords: "about OFinancial Markets, broker company, regulated broker, OFinancial",
  },
  "/faq": {
    title: "FAQ | Accounts, Funding, MT5, Withdrawals | OFinancial Markets",
    description: "Answers to the most common questions about OFinancial Markets account opening, MT5 platform, spreads, swaps, funding methods and withdrawals.",
    keywords: "broker FAQ, OFinancial FAQ, MT5 questions, broker withdrawals, account funding",
  },
  "/legal": {
    title: "Legal Centre | Policies & Agreements | OFinancial Markets",
    description: "All legal documents that govern your relationship with OFinancial Markets — Terms, Client Agreement, AML, Privacy, Cookies and Bonus Terms.",
    keywords: "OFinancial legal, broker terms, broker privacy policy, AML policy, client agreement, bonus terms",
  },
  "/how-it-works": {
    title: "How It Works | Open Account in 4 Hours | OFinancial Markets",
    description: "Open an account, fund it, download MT5 and trade. A three-step onboarding guide for new clients · under four hours to first trade.",
    keywords: "how to open broker account, MT5 onboarding, broker sign-up, OFinancial Markets onboarding",
  },
  "/platforms/compare": {
    title: "Trading Platforms | MT5 Desktop, Web & Mobile | OFinancial Markets",
    description: "Trade the same MT5 account across Windows, Mac, web browser and iOS/Android. Single login, synced positions, identical execution.",
    keywords: "MT5 broker, MetaTrader 5 platform, MT5 desktop web mobile",
  },
  "/platforms/mt5": {
    title: "MetaTrader 5 | Multi-Asset MT5 Broker | OFinancial Markets",
    description: "MetaTrader 5 desktop, Mac, web and mobile clients. EAs, copy-trading, custom indicators, sub-millisecond execution across FX, metals, indices and crypto CFDs.",
    keywords: "MT5 broker, MetaTrader 5 download, MT5 web terminal, MT5 mobile, MT5 EAs",
  },
  "/platforms/web": {
    title: "MT5 Web Terminal | Browser Trading | OFinancial Markets",
    description: "Trade your OFinancial Markets account directly in the browser. Full MT5 charting, order management and depth of market with no install required.",
    keywords: "MT5 web terminal, browser trading, MetaTrader 5 web, OFinancial web trading",
  },
  "/platforms/mobile": {
    title: "MT5 Mobile | iOS & Android Trading Apps | OFinancial Markets",
    description: "Trade on the move with the native MT5 mobile app. Full charting, biometric login, push alerts and one-tap trade management.",
    keywords: "MT5 mobile, MetaTrader 5 iOS, MetaTrader 5 Android, mobile broker app",
  },
  "/calculator": {
    title: "Trading Calculators | Pip, Margin & Profit | OFinancial Markets",
    description: "Free calculators for pip value, required margin and projected profit across all OFinancial Markets accounts. No sign-up needed.",
    keywords: "pip calculator, margin calculator, forex calculator, broker calculator, OFinancial calculator",
  },
  "/contact": {
    title: "Contact Us | Support, Partnerships, Press | OFinancial Markets",
    description: "Reach OFinancial Markets support, partnerships, press or compliance. We respond within 4 business hours.",
    keywords: "OFinancial contact, broker support, broker partnerships, broker press",
  },
  "/reviews": {
    title: "Reviews | Verified Client Stories | OFinancial Markets",
    description: "Verified client reviews and case studies from OFinancial Markets traders worldwide.",
    keywords: "OFinancial reviews, broker reviews, trader testimonials",
  },
  "/live-spreads": {
    title: "Live Spreads | Real-Time MT5 Pricing | OFinancial Markets",
    description: "Real-time spread feed across FX, metals, indices, crypto, energies and shares CFDs. Filter and search 1200+ instruments.",
    keywords: "live spreads, real-time spreads, broker spreads, MT5 spread feed",
  },
  "/symbols": {
    title: "Symbol Explorer | 1200+ Tradeable Instruments | OFinancial Markets",
    description: "Search and filter every instrument we offer — 1,000+ forex pairs, metals, indices, share CFDs, crypto and energies — with live bid, ask, spread and change %.",
    keywords: "symbol explorer, broker instruments list, all trading instruments, MT5 symbols, forex symbol list",
  },
  "/company-news": {
    title: "Company News | Announcements & Press Releases | OFinancial Markets",
    description: "Official announcements, press releases and milestones from the OFinancial group — regulation, products and company updates.",
    keywords: "OFinancial news, broker press release, OFinancial announcements, FSC Mauritius licence",
  },
  "/company-news/fsc-mauritius-licence": {
    title: "OFinancial Secures FSC Mauritius Licence | Company News",
    description: "OFinancial LLC is now regulated by the Financial Services Commission (FSC) of Mauritius as an Investment Dealer, Licence Number GB25205138 — expanding the group's global regulatory footprint.",
    keywords: "OFinancial FSC Mauritius, GB25205138, Mauritius Investment Dealer licence, regulated broker Mauritius, OFinancial press release",
  },
  "/market-hours": {
    title: "Market Hours | Live Forex Trading Sessions | OFinancial Markets",
    description: "Live trading-session clock for Sydney, Tokyo, London and New York plus full hours for forex, metals, indices, energies, shares and crypto.",
    keywords: "market hours, forex trading hours, trading sessions, open close times, live session clock",
  },
  "/markets/forex": {
    title: "Forex Trading | 78+ Pairs from 0.0 Pips | OFinancial Markets",
    description: "Trade 78+ forex pairs — majors, minors and exotics — with raw institutional spreads from 0.0 pips and leverage up to 1:1000 on MT5.",
    keywords: "forex broker, forex trading, FX pairs, EURUSD broker, raw spread forex, MT5 forex",
  },
  "/markets/metals": {
    title: "Metals Trading | Gold, Silver & Platinum CFDs | OFinancial Markets",
    description: "Trade gold (XAUUSD), silver (XAGUSD), platinum and palladium as CFDs. Leverage up to 1:500, tight spreads, MT5 platform.",
    keywords: "gold trading, XAUUSD, silver trading, XAGUSD, platinum CFD, metals broker",
  },
  "/markets/indices": {
    title: "Indices Trading | S&P 500, NASDAQ, DAX, FTSE | OFinancial Markets",
    description: "Trade major global indices as cash and futures CFDs — SP500, NAS100, WS30, DAX40, UK100, JPN225 and more, with leverage up to 1:100.",
    keywords: "indices broker, SP500 CFD, NASDAQ CFD, DAX broker, FTSE CFD, indices trading",
  },
  "/markets/crypto": {
    title: "Crypto Trading | Bitcoin, Ethereum, 90+ Coins | OFinancial Markets",
    description: "Trade Bitcoin, Ethereum and 70+ cryptocurrencies as CFDs around the clock. No wallet, no custody — pure MT5 execution with leverage up to 1:20.",
    keywords: "crypto broker, BTCUSD CFD, ETHUSD CFD, bitcoin trading, crypto CFDs, MT5 crypto",
  },
  "/markets/energies": {
    title: "Oil & Energies | WTI, Brent, Natural Gas | OFinancial Markets",
    description: "Trade WTI and Brent crude oil CFDs at OFinancial Markets. Tight spreads, leverage up to 1:100, full MT5 platform suite.",
    keywords: "oil trading, WTI broker, Brent broker, USOIL, UKOIL, natural gas CFD, energies broker",
  },
  "/markets/shares": {
    title: "Share CFDs | Apple, Tesla, NVIDIA & 750+ Stocks | OFinancial Markets",
    description: "Speculate on 750+ US, EU, UK and Asian shares as CFDs — no ownership, no commissions on Classic accounts, leverage up to 1:10.",
    keywords: "shares CFD broker, AAPL CFD, TSLA CFD, stock CFDs, NVIDIA CFD, ASML CFD",
  },
  "/leverage": {
    title: "Leverage | Dynamic Volume-Tiered up to 1:1000 | OFinancial Markets",
    description: "OFinancial Markets dynamic volume-tiered leverage — up to 1:1000 on FX majors (up to 1 lot of open volume) and gold (up to 0.20 lots), stepping down automatically as volume grows. Indices 1:100, crypto 1:20, share CFDs 1:10.",
    keywords: "broker leverage, 1:1000 leverage, dynamic leverage, forex leverage broker, MT5 leverage",
  },
  "/safety-of-funds": {
    title: "Safety of Funds | Segregated Tier-1 Bank Accounts | OFinancial Markets",
    description: "Your deposits are held in segregated tier-1 bank accounts, fully separated from OFinancial Markets operating capital, with negative balance protection.",
    keywords: "client fund safety, segregated accounts broker, tier-1 banks broker, negative balance protection, PCI DSS broker",
  },
  "/regulation": {
    title: "Regulation | Licensed & Authorized Broker | OFinancial Markets",
    description: "Our financial group is authorized and licensed in Antigua and Mauritius. Independent audits, AML/KYC compliance and segregated client funds.",
    keywords: "OFinancial regulation, broker license, regulated broker, MT5 regulated, AML KYC compliance",
  },
  "/funding": {
    title: "Funding & Withdrawals | Cards, Crypto, Wire | OFinancial Markets",
    description: "Fund your account with cards, e-wallets, crypto (USDT, BTC, ETH) or bank wire. No deposit fees. 24-hour withdrawal processing.",
    keywords: "broker funding methods, deposit broker, broker withdrawals, crypto deposits broker, MT5 funding",
  },
  "/withdrawals": {
    title: "Withdrawals | Same-Day Processing | OFinancial Markets",
    description: "How to withdraw from your OFinancial Markets account. Same-day processing, multiple methods, transparent fees disclosed up-front.",
    keywords: "broker withdrawals, fast withdrawals broker, withdrawal methods, MT5 withdrawals",
  },
  "/swap-rates": {
    title: "Swap Rates | Live Overnight Financing | OFinancial Markets",
    description: "Live swap (overnight financing) rates across FX, metals, indices, energies and crypto. Islamic swap-free accounts available.",
    keywords: "swap rates broker, overnight financing, broker swap, Islamic swap-free, rollover rates",
  },
  "/glossary": {
    title: "Trading Glossary | Forex & CFD Terms Explained | OFinancial Markets",
    description: "Every key forex and CFD trading term defined plainly — pips, lots, spreads, margins, leverage, hedging, slippage and more.",
    keywords: "trading glossary, forex terms, CFD glossary, trading definitions, pip definition, lot definition",
  },
  "/support": {
    title: "Support | 24/5 Live Chat, Email & Phone | OFinancial Markets",
    description: "Get help from OFinancial Markets support 24 hours a day, 5 days a week via live chat, email or phone. Multilingual coverage.",
    keywords: "OFinancial support, broker contact, 24/5 broker support, live chat broker",
  },
  "/why-ofinancial": {
    title: "Why OFinancial Markets | Institutional Execution for Retail Traders",
    description: "Why traders choose OFinancial Markets — raw spreads, sub-millisecond execution, segregated funds, 1200+ instruments and a single MT5 platform.",
    keywords: "why OFinancial, best MT5 broker, institutional broker, raw spread broker",
  },
  "/sentiment": {
    title: "Market Sentiment | Live Crowd Positioning | OFinancial Markets",
    description: "Live trader sentiment across major instruments — see what the crowd is buying or selling in real-time.",
    keywords: "market sentiment, broker sentiment, trader positioning, forex sentiment",
  },
  "/economic-calendar": {
    title: "Economic Calendar | Live Market Events | OFinancial Markets",
    description: "Track high-impact news, central-bank decisions, NFP, CPI and earnings in real time. Filter by impact, country and asset class.",
    keywords: "economic calendar, forex calendar, news events broker, NFP, CPI calendar",
  },
  "/partner": {
    title: "Partners | IB & Affiliate Programs | OFinancial Markets",
    description: "Earn per-lot rebates and CPA options on every active trader you refer. Real-time dashboard, dedicated account manager, transparent daily payouts.",
    keywords: "broker IB program, broker affiliate, forex partner program, MT5 IB, per-lot rebates, daily payouts",
  },
};

const LEGAL_SEO = {
  privacy: {
    title: "Privacy Policy · OFinancial Markets",
    description: "How OFinancial Markets collects, processes and protects your personal data including KYC, cookies, analytics and your privacy rights.",
    keywords: "OFinancial privacy policy, broker privacy, GDPR broker, data protection broker",
  },
  terms: {
    title: "Terms & Conditions · OFinancial Markets",
    description: "OFinancial Markets Terms & Conditions covering client accounts, trading conduct, fees, swaps, withdrawals and dispute resolution.",
    keywords: "OFinancial terms, broker terms, client agreement, trading terms",
  },
  "client-agreement": {
    title: "Client Agreement · OFinancial Markets",
    description: "The full Client Agreement between OFinancial Markets and its clients including rights, obligations, conflicts of interest and risk disclosures.",
    keywords: "OFinancial client agreement, broker client agreement",
  },
  cookies: {
    title: "Cookie Policy · OFinancial Markets",
    description: "How OFinancial Markets uses cookies for analytics, performance, language preference and security. Manage your preferences here.",
    keywords: "OFinancial cookies, broker cookie policy, GDPR cookies",
  },
  refund: {
    title: "Refund Policy · OFinancial Markets",
    description: "OFinancial Markets refund policy covering deposit corrections, duplicate transactions and dispute resolution. How to request a refund.",
    keywords: "OFinancial refund, broker refund policy, deposit refund",
  },
  aml: {
    title: "AML & KYC Policy · OFinancial Markets",
    description: "Our anti-money-laundering and know-your-customer policy outlining client onboarding checks, source-of-funds checks and ongoing monitoring.",
    keywords: "OFinancial AML, broker AML, KYC broker",
  },
  "bonus-terms": {
    title: "Bonus Terms · OFinancial Markets",
    description: "The terms and conditions that govern OFinancial Markets bonus offers, including the 50% First Deposit Bonus.",
    keywords: "OFinancial bonus terms, broker bonus terms, deposit bonus terms",
  },
};

export default function SEOHead() {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const origin = `${window.location.protocol}//${window.location.host}`;
    const pathname = location.pathname;
    let meta = ROUTE_SEO[pathname] || null;
    let isLegal = false;
    if (pathname.startsWith("/legal/")) {
      const slug = pathname.split("/")[2];
      if (LEGAL_SEO[slug]) {
        meta = LEGAL_SEO[slug];
        isLegal = true;
      }
    }

    // i18n override · for the top routes we publish a `seo.<key>` block
    // in every locale. When the current language has localized copy we
    // use it; otherwise we fall through to the English ROUTE_SEO above
    // so search engines still see meaningful tags.
    const I18N_SEO_KEY = {
      "/": "home",
      "/account-types": "accountTypes",
      "/first-deposit-bonus": "firstDepositBonus",
      "/platforms/mt5": "mt5",
      "/markets/forex": "forex",
      "/markets/metals": "metals",
      "/markets/indices": "indices",
      "/markets/crypto": "crypto",
      "/about": "about",
      "/contact": "contact",
      "/faq": "faq",
    };
    const i18nKey = I18N_SEO_KEY[pathname];
    if (i18nKey) {
      const tTitle = t(`seo.${i18nKey}.title`, { defaultValue: null });
      const tDesc  = t(`seo.${i18nKey}.description`, { defaultValue: null });
      if (tTitle || tDesc) {
        meta = {
          ...(meta || {}),
          title:       tTitle || meta?.title,
          description: tDesc  || meta?.description,
          keywords:    meta?.keywords,
        };
      }
    }

    if (meta) {
      document.title = meta.title;
      upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: meta.keywords });
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    }

    upsertCanonical(`${origin}${pathname}`);
    upsertHreflangLinks(pathname);
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: window.location.href });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: ({ en: "en_US", ar: "ar_SA", fr: "fr_FR", es: "es_ES", de: "de_DE", pt: "pt_PT", so: "so_SO" })[i18n.language] || "en_US",
    });

    if (pathname !== "/") {
      const parts = pathname.split("/").filter(Boolean);
      const items = [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      ];
      let acc = "";
      parts.forEach((p, idx) => {
        acc += `/${p}`;
        items.push({
          "@type": "ListItem",
          position: idx + 2,
          name: labelFor(p),
          item: `${origin}${acc}`,
        });
      });
      upsertJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
      });
    } else {
      removeJsonLd("ld-breadcrumb");
    }

    if (isLegal) {
      const slug = pathname.split("/")[2];
      const doc = LEGAL[slug];
      if (doc) {
        upsertJsonLd("ld-article", {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: doc.title,
          datePublished: "2024-01-01",
          dateModified: "2026-02-20",
          author: { "@type": "Organization", name: "OFinancial Markets" },
          publisher: {
            "@type": "Organization",
            name: "OFinancial Markets",
            logo: { "@type": "ImageObject", url: `${origin}/favicon.svg` },
          },
          description: doc.intro,
          inLanguage: i18n.language,
        });
      }
    } else {
      removeJsonLd("ld-article");
    }

    if (meta || pathname === "/") {
      upsertJsonLd("ld-webpage", {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: meta?.title || document.title,
        description: meta?.description || document.querySelector('meta[name="description"]')?.content,
        url: `${origin}${pathname}`,
        inLanguage: i18n.language,
        isPartOf: { "@type": "WebSite", name: "OFinancial Markets", url: `${origin}/` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: `${origin}/og-image.png`,
          width: 1200,
          height: 630,
        },
        publisher: {
          "@type": "Organization",
          name: "OFinancial Markets",
          logo: { "@type": "ImageObject", url: `${origin}/favicon.svg` },
        },
      });
    } else {
      removeJsonLd("ld-webpage");
    }

    // Organization JSON-LD · always emitted so every AI/search crawler
    // can fetch a single source of truth for the broker identity.
    upsertJsonLd("ld-organization", {
      "@context": "https://schema.org",
      "@type": ["Organization", "FinancialService"],
      name: "OFinancial Markets",
      legalName: "OFinancial Markets Ltd",
      url: `${origin}/`,
      logo: `${origin}/favicon.svg`,
      image: `${origin}/og-image.png`,
      slogan: "Trade markets like an institution.",
      description: "Licensed multi-asset online broker offering raw-spread access to forex, metals, indices, commodities, share CFDs and crypto on MetaTrader 5.",
      foundingDate: "2025",
      address: {
        "@type": "PostalAddress",
        addressCountry: "MU",
        addressLocality: "Port Louis",
        addressRegion: "Mauritius",
      },
      areaServed: { "@type": "Place", name: "Worldwide (subject to restricted-jurisdictions list)" },
      knowsAbout: [
        "forex trading", "MetaTrader 5", "raw spread broker",
        "indices CFD", "metals CFD", "crypto CFD", "share CFD",
        "negative balance protection", "segregated client funds",
      ],
      sameAs: [
        "https://twitter.com/ofinancial",
        "https://www.facebook.com/ofinancial",
        "https://www.linkedin.com/company/ofinancial-markets",
        "https://www.instagram.com/ofinancial",
        "https://www.youtube.com/@ofinancial",
      ],
      contactPoint: [
        { "@type": "ContactPoint", contactType: "customer support", email: "support@ofinancial.markets", availableLanguage: ["English","Arabic","French","Spanish","German","Portuguese"] },
        { "@type": "ContactPoint", contactType: "partners",         email: "partners@ofinancial.markets" },
        { "@type": "ContactPoint", contactType: "press",            email: "press@ofinancial.markets" },
        { "@type": "ContactPoint", contactType: "compliance",       email: "compliance@ofinancial.markets" },
      ],
      offers: {
        "@type": "OfferCatalog",
        name: "OFinancial Markets account types",
        itemListElement: [
          { "@type": "Offer", name: "Classic Account", url: `${origin}/account-types/classic`, description: "Floating spreads from 1.0 pips, no commission, min deposit $50." },
          { "@type": "Offer", name: "Premier Account", url: `${origin}/account-types/premier`, description: "Raw spreads from 0.0 pips, $3.00 per side commission, min deposit $1,000." },
          { "@type": "Offer", name: "Elite Account",   url: `${origin}/account-types/elite`,   description: "Raw spreads from 0.0 pips, $2.50 per side commission, min deposit $5,000." },
          { "@type": "Offer", name: "Social Account",  url: `${origin}/account-types/social`,  description: "Copy-trading account, follow signal-providers, min deposit $50." },
          { "@type": "Offer", name: "Cent Account",    url: `${origin}/account-types/cent`,    description: "Micro-lot cent (USC) account, floating spreads from 1.0 pips, no commission, min deposit $50." },
        ],
      },
    });

    // WebSite + SearchAction · enables sitelinks-search-box result on
    // Google AND lets AI assistants discover internal site-search.
    upsertJsonLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "OFinancial Markets",
      alternateName: ["OFinancial", "OFM"],
      url: `${origin}/`,
      inLanguage: i18n.language,
      publisher: { "@type": "Organization", name: "OFinancial Markets" },
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${origin}/symbols?search={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    });

    if (pathname.startsWith("/platforms/")) {
      const platformMap = {
        "/platforms/mt5":     { name: "MetaTrader 5 at OFinancial Markets", desc: "MT5 desktop, web and mobile · multi-asset trading at OFinancial Markets." },
        "/platforms/web":     { name: "MT5 Web Terminal at OFinancial Markets", desc: "Browser-based MT5 client at OFinancial Markets. No install, full charting and order management." },
        "/platforms/mobile":  { name: "MT5 Mobile at OFinancial Markets", desc: "Native iOS and Android MT5 apps at OFinancial Markets." },
        "/platforms/compare": { name: "MT5 Platform Family at OFinancial Markets", desc: "MT5 desktop, web and mobile clients all sharing a single login at OFinancial Markets." },
      };
      const sv = platformMap[pathname];
      if (sv) {
        upsertJsonLd("ld-service", {
          "@context": "https://schema.org",
          "@type": "Service",
          name: sv.name,
          description: sv.desc,
          serviceType: "Online retail brokerage platform access",
          provider: { "@type": "Organization", name: "OFinancial Markets", url: `${origin}/` },
          areaServed: "Worldwide (excluding restricted jurisdictions)",
          audience: { "@type": "Audience", audienceType: "Active retail and institutional traders" },
          url: `${origin}${pathname}`,
        });
      } else {
        removeJsonLd("ld-service");
      }
    } else {
      removeJsonLd("ld-service");
    }

    if (pathname === "/faq" || pathname === "/") {
      try {
        const flat = (FAQ_CATEGORIES || []).flatMap((cat) => cat.items || []);
        const mainEntity = flat.slice(0, 30).map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        }));
        if (mainEntity.length) {
          upsertJsonLd("ld-faq", {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity,
          });
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          if (process.env.NODE_ENV !== "production") console.debug("SEOHead: failed to build FAQPage schema", err);
        }
      }
    } else {
      removeJsonLd("ld-faq");
    }
  }, [location.pathname, i18n.language, params.slug, t]);

  // Backend SEO override (admin-managed) + dynamic aggregateRating.
  useEffect(() => {
    api.get("/seo").then(({ data }) => {
      if (!data) return;
      // aggregateRating · server computes from db.reviews (≥3 reviews
      // required by Google) so the Organization schema reflects real
      // data without us hardcoding it. Merges into the already-emitted
      // ld-organization script tag so a single source of truth.
      if (data.aggregate_rating && data.aggregate_rating.reviewCount >= 3) {
        const orgScript = document.getElementById("ld-organization");
        if (orgScript) {
          try {
            const org = JSON.parse(orgScript.textContent);
            org.aggregateRating = {
              "@type": "AggregateRating",
              ratingValue: data.aggregate_rating.ratingValue,
              reviewCount: data.aggregate_rating.reviewCount,
              bestRating: data.aggregate_rating.bestRating || 5,
              worstRating: data.aggregate_rating.worstRating || 1,
            };
            orgScript.textContent = JSON.stringify(org);
          } catch { /* malformed cached JSON · safe to ignore */ }
        }
      }
      if (location.pathname !== "/") return;
      if (data.title) document.title = data.title;
      if (data.description) upsertMeta('meta[name="description"]', { name: "description", content: data.description });
      if (data.keywords) upsertMeta('meta[name="keywords"]', { name: "keywords", content: data.keywords });
      if (data.robots) upsertMeta('meta[name="robots"]', { name: "robots", content: data.robots });
      if (data.og_title) upsertMeta('meta[property="og:title"]', { property: "og:title", content: data.og_title });
      if (data.og_description) upsertMeta('meta[property="og:description"]', { property: "og:description", content: data.og_description });
      if (data.og_image) upsertMeta('meta[property="og:image"]', { property: "og:image", content: data.og_image });
      if (data.canonical_url) upsertCanonical(data.canonical_url);
      if (data.twitter_card) upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: data.twitter_card });
      if (data.og_title) upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: data.og_title });
      if (data.og_description) upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: data.og_description });
      if (data.og_image) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: data.og_image });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-account Product OG + JSON-LD on /account-types/{slug} so sharing
  // a tier on WhatsApp/X/Slack renders a rich preview card with the min
  // deposit and spread, and Google can show a Product knowledge panel
  // with offer details + star rating.
  useEffect(() => {
    const origin = `${window.location.protocol}//${window.location.host}`;
    const m = location.pathname.match(/^\/account-types\/(classic|premier|elite|social|islamic|cent)\b/);
    if (!m) {
      // Reset og:type back to website for non-product pages · idempotent.
      upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      removeJsonLd("ld-product");
      return;
    }
    const slug = m[1];
    const acct = ACCOUNTS[slug];
    if (!acct) return;
    const parseMoney = (v) => {
      const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
      return Number.isFinite(n) ? n : 0;
    };
    const minDeposit = parseMoney(acct.minDeposit);
    const commission = parseMoney(acct.commission);

    // OG / Twitter as Product · Facebook + WhatsApp + X all read these
    upsertMeta('meta[property="og:type"]',                  { property: "og:type",                  content: "product" });
    upsertMeta('meta[property="product:price:amount"]',     { property: "product:price:amount",     content: String(minDeposit) });
    upsertMeta('meta[property="product:price:currency"]',   { property: "product:price:currency",   content: "USD" });
    upsertMeta('meta[property="product:availability"]',     { property: "product:availability",     content: "in stock" });
    upsertMeta('meta[property="product:condition"]',        { property: "product:condition",        content: "new" });
    upsertMeta('meta[property="product:retailer_item_id"]', { property: "product:retailer_item_id", content: `ofm-${slug}` });
    upsertMeta('meta[name="twitter:label1"]',               { name:     "twitter:label1",           content: "Min Deposit" });
    upsertMeta('meta[name="twitter:data1"]',                { name:     "twitter:data1",            content: `$${minDeposit.toLocaleString()}` });
    upsertMeta('meta[name="twitter:label2"]',               { name:     "twitter:label2",           content: "Typical spread · Max leverage" });
    upsertMeta('meta[name="twitter:data2"]',                { name:     "twitter:data2",            content: `${acct.spreadFrom || acct.spread || "—"} pip · ${acct.leverage}` });

    upsertJsonLd("ld-product", {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${origin}${location.pathname}#product`,
      name: `${acct.label} Account · OFinancial Markets`,
      description: acct.blurb || acct.tagline || `${acct.label} trading account with ${acct.spreadFrom || acct.spread || "raw"} pip spreads, ${acct.leverage} leverage and a $${minDeposit.toLocaleString()} minimum deposit.`,
      brand: { "@type": "Brand", name: "OFinancial Markets" },
      category: "Trading account",
      url: `${origin}${location.pathname}`,
      image: `${origin}/og-image.png`,
      sku: `ofm-${slug}`,
      offers: {
        "@type": "Offer",
        url: `${origin}${location.pathname}`,
        price: String(minDeposit),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
        seller: { "@type": "Organization", name: "OFinancial Markets" },
      },
      additionalProperty: [
        { "@type": "PropertyValue", name: "Typical spread (EURUSD)", value: acct.spreadFrom ? `${acct.spreadFrom} pip` : acct.spread },
        { "@type": "PropertyValue", name: "Commission per side",     value: acct.commission },
        { "@type": "PropertyValue", name: "Maximum leverage",        value: acct.leverage },
        { "@type": "PropertyValue", name: "Execution",               value: acct.execution || "Market" },
      ].filter(p => p.value !== undefined && p.value !== null && p.value !== ""),
    });
  }, [location.pathname]);

  return null;
}
